import * as THREE from 'three';
import { SketchChamferType } from './SketchCornerDrawingTypes';
import {normalizeArcAngles} from '../../tool/normalizeArcAngles';
import type {Viewer} from '../../viewer/Viewer';
import {TangentConstraint} from '../solver/constraint/relationDefinition';
import {
  LineAngleConstraint,
  LineIntersectionConstraint,
} from '../solver/constraint/relationDefinition/line';
import {PointDistanceConstraint} from '../solver/constraint/relationDefinition/point';
import {
  createSketchCloneContext,
  SketchArcObject,
  SketchLineObject,
  SketchPointBoxSelectionRole,
  SketchPointKind,
  SketchPointObject,
} from '../../mesh/sketch';
import {isSketchArcObject, isSketchLineObject} from '../../mesh/guards';
import {createSketchEntityPersistentId} from '../identity/SketchPersistentIdFactory';
import type {SketchArcDefinition} from '../../mesh/sketch';
import {isDashDotLIneNodeMaterial, LineMaterialFactory, LineMaterialState, PointsMaterialFactory} from '../../material';
import type {
  SketchCornerAnalysis,
  SketchCornerLineData,
  SketchCornerLineUsage,
  SketchCornerOperationOptions,
  SketchFilletGeometryResult,
  SketchSharedCornerPoint,
} from './SketchCornerTypes';
import type {
  SketchConstraintControlPointTopologyChange,
  SketchConstraintControlPointTopologyMapping,
} from '../../../types/sketch/SketchConstraintTopologyTypes';
import {SketchSymmetricControlPointTopologyService} from './SketchSymmetricControlPointTopologyService';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {
  SKETCH_CLOSE_TOLERANCE,
  SKETCH_GEOMETRY_TOLERANCE,
  SketchCornerPointRole,
} from './SketchCornerTypes';

/**
 * 草图角点操作上下文。
 * 职责：
 * 1. 提供圆角/倒角共享的几何解析能力；
 * 2. 提供创建/替换草图实体与控制点的能力；
 * 3. 提供倒角类型标准化与圆弧方向计算能力。
 */
export class SketchCornerOperationContext {
  /** Viewer 运行对象。 */
  private readonly render: Viewer;
  /** 实体级对称关系的控制点拓扑传播服务。 */
  private readonly symmetricTopologyService = new SketchSymmetricControlPointTopologyService();

  /**
   * 构造函数。
   * @param render Viewer 运行对象。
   */
  public constructor(render: Viewer) {
    this.render = render;
  }

  /**
   * 获取当前参考面。
   * @returns 当前参考面。
   */
  private getReferencePlaneOrThrow() {
    /**
     * 草图几何属于模型状态，必须优先读取活动草图持有的参考面。
     * `currentReferencePlane` 是视图态投影上下文，只作为没有活动草图的兼容入口。
     */
    const referencePlane = this.render.sketchGroupManager?.activeSketchGroup?.referencePlane
      ?? this.render.currentReferencePlane;
    if (!referencePlane) {
      throw new Error('没有基准面');
    }
    return referencePlane;
  }

  /**
   * 解析线段基础数据。
   * @param line 线对象。
   * @returns 线段数据。
   */
  public resolveCornerLineData(line: SketchLineObject): SketchCornerLineData | null {
    const referencePlane = line.referencePlane;
    const startPoint = line.startPoint;
    const endPoint = line.endPoint;
    if (!startPoint || !endPoint) {
      return null;
    }

    const startWorld = startPoint.getPosition();
    const endWorld = endPoint.getPosition();
    const length = startWorld.distanceTo(endWorld);
    if (!Number.isFinite(length) || length <= SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }

    return {
      line,
      startPoint,
      endPoint,
      startWorld,
      endWorld,
      startLocal: referencePlane.worldToLocal(startWorld),
      endLocal: referencePlane.worldToLocal(endWorld),
      length,
      isConstruction: line.isConstruction,
    };
  }

  //------ 两直线圆角命中选支修复 开始------
  /**
   * 解析两条线的角点（共享端点优先，其次使用延长线交点）。
   *
   * 设计说明：
   * 1. 共享端点只有一个确定角点，继续按端点拓扑解析；
   * 2. 虚拟交点可能对应四组保留射线，必须使用两次鼠标命中位置选择用户实际点击的分支；
   * 3. 命中位置缺失、非法或过于接近交点时回退到原有最近端点规则，兼容倒角和历史调用方。
   *
   * @param firstLineData 第一条线。
   * @param secondLineData 第二条线。
   * @param firstHitWorld 第一条线的鼠标命中世界坐标。
   * @param secondHitWorld 第二条线的鼠标命中世界坐标。
   * @returns 角点解析结果。
   */
  public resolveCornerAnalysis(
    firstLineData: SketchCornerLineData,
    secondLineData: SketchCornerLineData,
    firstHitWorld: THREE.Vector3 | null = null,
    secondHitWorld: THREE.Vector3 | null = null,
  ): SketchCornerAnalysis | null {
    const referencePlane = firstLineData.line.referencePlane;
    if (referencePlane !== secondLineData.line.referencePlane) {
      return null;
    }
    const sharedCornerPoint = this.resolveSharedCornerPoint(firstLineData, secondLineData);
    if (sharedCornerPoint) {
      const firstUsage = this.resolveLineUsageByCornerRole(firstLineData, sharedCornerPoint.firstCornerRole);
      const secondUsage = this.resolveLineUsageByCornerRole(secondLineData, sharedCornerPoint.secondCornerRole);
      if (!firstUsage || !secondUsage) {
        return null;
      }
      return {
        cornerWorld: sharedCornerPoint.cornerWorld,
        cornerLocal: sharedCornerPoint.cornerLocal,
        firstUsage,
        secondUsage,
      };
    }

    const intersectionLocal = this.resolveInfiniteLineIntersectionLocal(firstLineData, secondLineData);
    if (!intersectionLocal) {
      return null;
    }

    // 只有虚拟交点存在多分支歧义，因此在此处把世界命中坐标投影到参考面。
    const firstHitLocal = this.resolveFiniteHitLocal(firstHitWorld, referencePlane);
    const secondHitLocal = this.resolveFiniteHitLocal(secondHitWorld, referencePlane);
    const firstUsage = this.resolveLineUsageByVirtualCorner(
      firstLineData,
      intersectionLocal,
      firstHitLocal,
      referencePlane,
    );
    const secondUsage = this.resolveLineUsageByVirtualCorner(
      secondLineData,
      intersectionLocal,
      secondHitLocal,
      referencePlane,
    );
    if (!firstUsage || !secondUsage) {
      return null;
    }

    return {
      cornerWorld: referencePlane.localToWorld(intersectionLocal),
      cornerLocal: intersectionLocal.clone(),
      firstUsage,
      secondUsage,
    };
  }
  //------ 两直线圆角命中选支修复 结束------

  /**
   * 解析两条线的夹角（弧度）。
   * @param cornerAnalysis 角点解析结果。
   * @returns 夹角；无效时返回 null。
   */
  public resolveCornerAngle(cornerAnalysis: SketchCornerAnalysis): number | null {
    const dir1 = cornerAnalysis.firstUsage.directionLocal.clone().normalize();
    const dir2 = cornerAnalysis.secondUsage.directionLocal.clone().normalize();
    const dot = THREE.MathUtils.clamp(dir1.dot(dir2), -1, 1);
    const angle = Math.acos(dot);
    if (!Number.isFinite(angle) || angle < 1e-5 || Math.abs(Math.PI - angle) < 1e-4) {
      return null;
    }
    return angle;
  }

  /**
   * 判断修剪距离是否在线段可用范围内。
   * @param trimDistance 修剪距离。
   * @param usage 线段使用信息。
   * @returns 是否有效。
   */
  public isTrimDistanceWithinLineRange(trimDistance: number, usage: SketchCornerLineUsage): boolean {
    return trimDistance < usage.farDistanceToCorner - SKETCH_GEOMETRY_TOLERANCE;
  }

  /**
   * 按预览算法求解圆角几何。
   * @param cornerAnalysis 角点解析结果。
   * @param angle 两线夹角。
   * @returns 圆角几何结果；失败时返回 null。
   */
  public resolveFilletGeometry(
    cornerAnalysis: SketchCornerAnalysis,
    angle: number,
    radius: number,
  ): SketchFilletGeometryResult | null {
    const referencePlane = cornerAnalysis.firstUsage.lineData.line.referencePlane;
    if (referencePlane !== cornerAnalysis.secondUsage.lineData.line.referencePlane) {
      return null;
    }
    const dir1 = cornerAnalysis.firstUsage.directionLocal.clone().normalize();
    const dir2 = cornerAnalysis.secondUsage.directionLocal.clone().normalize();
    const trimDistance = radius / Math.tan(angle / 2);
    if (!Number.isFinite(trimDistance) || trimDistance <= SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }

    const trimPoint1Local = cornerAnalysis.cornerLocal.clone().addScaledVector(dir1, trimDistance);
    const trimPoint2Local = cornerAnalysis.cornerLocal.clone().addScaledVector(dir2, trimDistance);
    const bisectorLocal = dir1.clone().add(dir2);
    if (bisectorLocal.lengthSq() < SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }
    bisectorLocal.normalize();

    const centerDistance = radius / Math.sin(angle / 2);
    if (!Number.isFinite(centerDistance) || centerDistance <= SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }
    const centerLocal = cornerAnalysis.cornerLocal.clone().addScaledVector(bisectorLocal, centerDistance);
    const trimPoint1World = referencePlane.localToWorld(trimPoint1Local);
    const trimPoint2World = referencePlane.localToWorld(trimPoint2Local);
    const centerWorld = referencePlane.localToWorld(centerLocal);

    const startAngle = Math.atan2(trimPoint1Local.y - centerLocal.y, trimPoint1Local.x - centerLocal.x);
    const finishAngle = Math.atan2(trimPoint2Local.y - centerLocal.y, trimPoint2Local.x - centerLocal.x);
    const normalized = this.resolveFilletArcDirectionByBisector(startAngle, finishAngle, bisectorLocal);
    const arcPoints = this.buildArcWorldPoints(
      centerLocal,
      radius,
      normalized.start,
      normalized.angleRange,
      normalized.clockwise,
      referencePlane,
    );
    if (arcPoints.length < 2) {
      return null;
    }

    return {
      trimPoint1Local,
      trimPoint2Local,
      trimPoint1World,
      trimPoint2World,
      centerLocal,
      centerWorld,
      startAngle: normalized.start,
      endAngle: normalized.end,
      clockwise: normalized.clockwise,
      arcPoints,
    };
  }

  /**
   * 根据角平分线方向解析圆弧方向。
   * @param startAngle 起始角度。
   * @param endAngle 结束角度。
   * @param bisectorLocal 角平分线方向。
   * @returns 圆弧方向信息。
   */
  public resolveFilletArcDirectionByBisector(
    startAngle: number,
    endAngle: number,
    bisectorLocal: THREE.Vector2,
  ): {start: number; end: number; angleRange: number; clockwise: boolean} {
    const normalizedBisector = bisectorLocal.clone();
    const hasBisector = normalizedBisector.lengthSq() > 1e-12;
    if (hasBisector) {
      normalizedBisector.normalize();
    }

    const candidates = [true, false].map((clockwise) => {
      const normalized = normalizeArcAngles(startAngle, endAngle, clockwise);
      const middleAngle = clockwise
        ? normalized.start - normalized.angleRange * 0.5
        : normalized.start + normalized.angleRange * 0.5;
      const middleDirection = new THREE.Vector2(Math.cos(middleAngle), Math.sin(middleAngle));

      let score = 0;
      if (hasBisector) {
        score += middleDirection.dot(normalizedBisector);
      }
      if (normalized.angleRange > Math.PI + 1e-6) {
        score += 10;
      }
      score += normalized.angleRange * 1e-3;

      return {
        ...normalized,
        clockwise,
        score,
      };
    });

    candidates.sort((a, b) => a.score - b.score);
    const best = candidates[0];
    return {
      start: best.start,
      end: best.end,
      angleRange: best.angleRange,
      clockwise: best.clockwise,
    };
  }

  /**
   * 标准化倒角类型。
   * @param type 输入类型。
   * @returns 标准化类型。
   */
  public normalizeChamferType(type: SketchCornerOperationOptions['chamferType']): SketchCornerOperationOptions['chamferType'] {
    if (
      type === SketchChamferType.AngleDistance
      || type === SketchChamferType.DistanceDistance
      || type === SketchChamferType.EqualDistance
    ) {
      return type;
    }
    return SketchChamferType.EqualDistance;
  }

  /**
   * 使用新的控制点替换线对象角点侧端点。
   * @param line 目标线对象。
   * @param cornerRole 角点所在端点角色。
   * @param point 新控制点。
   */
  public replaceLineCornerControlPoint(
    line: SketchLineObject,
    cornerRole: SketchCornerPointRole,
    point: SketchPointObject,
  ): void {
    const controlPointIndex = cornerRole === SketchCornerPointRole.Start ? 0 : 1;
    this.replaceLineControlPointWithTopology(line, controlPointIndex, point, new Set<number>());
  }

  //------ 直线圆弧草图圆角端点替换 开始------
  /**
   * 使用新的控制点和圆弧定义替换源圆弧角点侧端点。
   *
   * @param arc 目标源圆弧。
   * @param cornerRole 被替换端点角色。
   * @param point 新切点控制点。
   * @param definition 修剪后的源圆弧定义。
   */
  public replaceArcCornerControlPoint(
    arc: SketchArcObject,
    cornerRole: SketchCornerPointRole,
    point: SketchPointObject,
    definition: Required<SketchArcDefinition>,
  ): void {
    const controlPointIndex = cornerRole === SketchCornerPointRole.Start ? 1 : 2;
    this.replaceArcControlPointWithTopology(
      arc,
      controlPointIndex,
      point,
      definition,
      new Set<number>(),
    );
  }
  //------ 直线圆弧草图圆角端点替换 结束------

  /**
   * 替换直线控制点，并把同一拓扑变化传播到其它实体级对称关系目标。
   * @param line 发生变化的直线实体。
   * @param controlPointIndex 起点为 0，终点为 1。
   * @param point 新控制点。
   * @param visitedRelationIds 当前传播链已处理的关系 ID。
   */
  private replaceLineControlPointWithTopology(
    line: SketchLineObject,
    controlPointIndex: number,
    point: SketchPointObject,
    visitedRelationIds: Set<number>,
  ): void {
    const previousPoint = controlPointIndex === 0 ? line.startPoint : line.endPoint;
    if (previousPoint === point) {
      return;
    }
    const mappings = this.resolveSymmetricTopologyMappings({
      entity: line,
      controlPointIndex,
      previousPoint,
      replacementPoint: point,
    }, visitedRelationIds);
    if (controlPointIndex === 0) {
      line.setStartPoint(point);
    } else if (controlPointIndex === 1) {
      line.setEndPoint(point);
    } else {
      throw new Error(`草图直线控制点拓扑索引非法: ${controlPointIndex}`);
    }
    this.applySymmetricTopologyMappings(mappings, visitedRelationIds);
  }

  /**
   * 替换圆弧端点，并把同一拓扑变化传播到其它实体级对称关系目标。
   * @param arc 发生变化的圆弧实体。
   * @param controlPointIndex 起点为 1，终点为 2。
   * @param point 新控制点。
   * @param definition 权威侧圆弧替换后定义；关系映射侧无需提供。
   * @param visitedRelationIds 当前传播链已处理的关系 ID。
   */
  private replaceArcControlPointWithTopology(
    arc: SketchArcObject,
    controlPointIndex: number,
    point: SketchPointObject,
    definition: SketchArcDefinition | undefined,
    visitedRelationIds: Set<number>,
  ): void {
    const previousPoint = controlPointIndex === 1 ? arc.getStartPoint() : arc.getEndPoint();
    if (previousPoint === point) {
      return;
    }
    const mappings = this.resolveSymmetricTopologyMappings({
      entity: arc,
      controlPointIndex,
      previousPoint,
      replacementPoint: point,
    }, visitedRelationIds);
    if (controlPointIndex === 1) {
      arc.setStartPoint(point, definition);
    } else if (controlPointIndex === 2) {
      arc.setEndPoint(point, definition);
    } else {
      throw new Error(`草图圆弧控制点拓扑索引非法: ${controlPointIndex}`);
    }
    this.applySymmetricTopologyMappings(mappings, visitedRelationIds);
  }

  /**
   * 在修改权威实体前解析本轮尚未处理的对称拓扑映射。
   * @param change 当前控制点替换。
   * @param visitedRelationIds 当前传播链已处理的关系 ID。
   * @returns 本轮需要执行的映射。
   */
  private resolveSymmetricTopologyMappings(
    change: SketchConstraintControlPointTopologyChange,
    visitedRelationIds: ReadonlySet<number>,
  ): SketchConstraintControlPointTopologyMapping[] {
    return this.symmetricTopologyService.resolveMappings(change)
      .filter((mapping) => !visitedRelationIds.has(mapping.relationId));
  }

  /**
   * 创建并替换对称侧控制点；递归传播其它尚未处理的实体级关系。
   * @param mappings 本轮关系映射。
   * @param visitedRelationIds 当前传播链已处理的关系 ID。
   */
  private applySymmetricTopologyMappings(
    mappings: readonly SketchConstraintControlPointTopologyMapping[],
    visitedRelationIds: Set<number>,
  ): void {
    mappings.forEach((mapping) => {
      visitedRelationIds.add(mapping.relationId);
      if (isSketchLineObject(mapping.targetEntity)) {
        const targetPoint = mapping.targetControlPointIndex === 0
          ? mapping.targetEntity.startPoint
          : mapping.targetControlPointIndex === 1
            ? mapping.targetEntity.endPoint
            : null;
        if (!targetPoint) {
          throw new Error(`草图直线控制点拓扑映射索引非法: ${mapping.targetControlPointIndex}`);
        }
        const mappedPoint = this.createMappedControlPoint(mapping.targetWorldPosition, targetPoint);
        this.replaceLineControlPointWithTopology(
          mapping.targetEntity,
          mapping.targetControlPointIndex,
          mappedPoint,
          visitedRelationIds,
        );
        this.removeDetachedPoint(targetPoint);
        return;
      }
      if (isSketchArcObject(mapping.targetEntity)) {
        const targetPoint = mapping.targetControlPointIndex === 1
          ? mapping.targetEntity.getStartPoint()
          : mapping.targetControlPointIndex === 2
            ? mapping.targetEntity.getEndPoint()
            : null;
        if (!targetPoint) {
          throw new Error(`草图圆弧控制点拓扑映射索引非法: ${mapping.targetControlPointIndex}`);
        }
        const mappedPoint = this.createMappedControlPoint(mapping.targetWorldPosition, targetPoint);
        this.replaceArcControlPointWithTopology(
          mapping.targetEntity,
          mapping.targetControlPointIndex,
          mappedPoint,
          undefined,
          visitedRelationIds,
        );
        this.removeDetachedPoint(targetPoint);
        return;
      }
      throw new Error(`Corner 不支持当前对称拓扑目标: ${mapping.targetEntity.uuid}`);
    });
  }

  /**
   * 创建并挂载对称关系映射产生的新控制点。
   * @param position 新控制点世界坐标。
   * @param previousPoint 被替换点，用于继承角色、材质与所在对象树。
   * @returns 已挂载的新控制点。
   */
  private createMappedControlPoint(
    position: THREE.Vector3,
    previousPoint: SketchPointObject,
  ): SketchPointObject {
    // 对称侧必须获得新逻辑身份；瞬时复用规范点材质，避免一次拓扑拆分创建额外 GPU 资源。
    const point = previousPoint.cloneSelf(createSketchCloneContext(undefined, true));
    point.persistentId = createSketchEntityPersistentId();
    point.syncPositionSilently(position);
    this.render.interactionStateManager?.syncExternalMaterialSnapshot(point);
    if (previousPoint.parent) {
      this.render.addObject(point, previousPoint.parent);
    } else {
      this.render.addSketchObject(point);
    }
    return point;
  }

  /**
   * 在控制点已不再连接任何边时，将其从场景中移除。
   * @param point 目标控制点。
   */
  public removeDetachedPoint(point: SketchPointObject): void {
    if (point.getConnectedEdgesSnapshot().length > 0) {
      return;
    }
    if (point.parent) {
      point.parent.remove(point);
    }
  }

  /**
   * 创建控制点。
   * @param position 控制点世界坐标。
   * @param boxSelectionRole 控制点框选角色；默认保持普通控制点不参与框选。
   * @param referencePlane 控制点所属草图参考面；默认使用活动草图参考面。
   * @returns 控制点对象。
   */
  public createCornerControlPoint(
    position: THREE.Vector3,
    boxSelectionRole: SketchPointBoxSelectionRole = SketchPointBoxSelectionRole.Default,
    referencePlane: ReferencePlane = this.getReferencePlaneOrThrow(),
  ): SketchPointObject {
    return new SketchPointObject({
      position: position.clone(),
      pointKind: SketchPointKind.ControlPoint,
      boxSelectionRole,
      material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
      referencePlane,
    });
  }

  /**
   * 创建草图线实体。
   * @param start 起点世界坐标。
   * @param finish 终点世界坐标。
   * @param isConstruction 是否构造线。
   * @param startPoint 可选复用起点控制点。
   * @param finishPoint 可选复用终点控制点。
   * @param referencePlane 新线所属草图参考面；默认使用活动草图参考面。
   * @returns 线对象与控制点。
   */
  public createSketchLineEntity(
    start: THREE.Vector3,
    finish: THREE.Vector3,
    isConstruction: boolean,
    startPoint?: SketchPointObject,
    finishPoint?: SketchPointObject,
    referencePlane: ReferencePlane = this.getReferencePlaneOrThrow(),
  ): {line: SketchLineObject; controlPoints: SketchPointObject[]} {
    const material = isConstruction
      ? LineMaterialFactory.createConstructionLineMaterial()
      : LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default);
    const resolvedStartPoint = startPoint ?? this.createCornerControlPoint(start, undefined, referencePlane);
    const resolvedFinishPoint = finishPoint ?? this.createCornerControlPoint(finish, undefined, referencePlane);
    const line = new SketchLineObject({
      startPoint: resolvedStartPoint,
      endPoint: resolvedFinishPoint,
      material,
      referencePlane,
    });
    if (material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(material)) {
      line.computeLineDistances();
    }
    return {
      line,
      controlPoints: [resolvedStartPoint, resolvedFinishPoint],
    };
  }

  /**
   * 创建草图圆弧实体。
   * @param isConstruction 是否构造线。
   * @param points 弧线点集。
   * @param params 弧线参数。
   * @returns 圆弧对象与控制点。
   */
  public createSketchArcEntity(
    isConstruction: boolean,
    points: THREE.Vector3[],
    params: {
      startPoint?: SketchPointObject;
      finishPoint?: SketchPointObject;
      center: THREE.Vector3;
      radius: number;
      startAngle: number;
      endAngle: number;
      clockwise: boolean;
      referencePlane?: ReferencePlane;
    },
  ): {arc: SketchArcObject; controlPoints: SketchPointObject[]} {
    const material = isConstruction
      ? LineMaterialFactory.createConstructionLineMaterial()
      : LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default);
    const arcStartWorld = points[0];
    const arcFinishWorld = points[points.length - 1];
    const referencePlane = params.referencePlane ?? this.getReferencePlaneOrThrow();
    const startControlPoint = params.startPoint
      ?? this.createCornerControlPoint(arcStartWorld, undefined, referencePlane);
    const finishControlPoint = params.finishPoint
      ?? this.createCornerControlPoint(arcFinishWorld, undefined, referencePlane);
    const centerControlPoint = this.createCornerControlPoint(params.center, undefined, referencePlane);

    const arc = new SketchArcObject({
      startPoint: startControlPoint,
      endPoint: finishControlPoint,
      centerPoint: centerControlPoint,
      material,
      referencePlane,
      definition: {
        centerWorld: params.center.clone(),
        radius: params.radius,
        startAngle: params.startAngle,
        endAngle: params.endAngle,
        clockwise: params.clockwise,
        segments: 64,
      },
    });
    arc.updateArc();

    // 圆角创建时，起点和终点控制点需要同时被原线与圆弧共享。
    if (params.startPoint && startControlPoint.parent === arc) {
      arc.remove(startControlPoint);
    }
    if (params.finishPoint && finishControlPoint.parent === arc) {
      arc.remove(finishControlPoint);
    }
    return {
      arc,
      controlPoints: [startControlPoint, finishControlPoint, centerControlPoint],
    };
  }

  //------ 圆角默认约束支持 开始------
  /**
   * 为圆角创建的圆弧挂载默认几何约束。
   * 说明：
   * 1. 圆弧挂载一条半径约束，锁定初始半径；
   * 2. 圆弧分别与两个源实体挂载相切约束；
   * 3. 源实体允许直线或圆弧，使直线—直线与直线—圆弧共用同一约束出口；
   * 4. 本上下文只建立候选关系，完整活动草图由命令控制器统一参数求解。
   * @param arc 圆角圆弧对象。
   * @param firstSource 第一条源实体。
   * @param secondSource 第二条源实体。
   * @param radius 圆角半径。
   */
  public bindDefaultConstraintsForFilletArc(
    arc: SketchArcObject,
    firstSource: SketchFilletSourceObject,
    secondSource: SketchFilletSourceObject,
  ): void {
    /**
     * 半径不在这里挂匿名关系：SW 的草图圆角默认带**半径尺寸**，
     * 半径锁由自动 driving 尺寸直接编译为参数方程；关系面板只投影只读快照，
     * 同一物理量只允许一个锁——再挂一条匿名半径关系就是重复约束。
     */
    // 绑定圆角与第一个源实体的相切约束。
    this.bindTangentConstraintBetweenSourceAndFillet(firstSource, arc);
    // 再绑定圆角与第二个源实体的相切约束。
    this.bindTangentConstraintBetweenSourceAndFillet(secondSource, arc);
  }

  /**
   * 绑定源实体与圆角圆弧之间的相切约束。
   * @param source 源直线或源圆弧。
   * @param arc 新建圆角圆弧。
   */
  private bindTangentConstraintBetweenSourceAndFillet(
    source: SketchFilletSourceObject,
    arc: SketchArcObject,
  ): void {
    // 创建一条新的相切约束实例，避免与其它关系复用同一对象。
    const tangentConstraint = new TangentConstraint();
    source.constraints.addRelation(
      tangentConstraint as Parameters<typeof source.constraints.addRelation>[0],
    );
    arc.constraints.addRelation(
      tangentConstraint as Parameters<typeof arc.constraints.addRelation>[0],
    );

	/** 相切候选不在对象层直接传播，等待圆角命令完成全部关系与实体挂载后统一求解。 */
  }
  //------ 圆角默认约束支持 结束------

  //------ 倒角交叉点关系支持 开始------
  /**
   * 绑定两条线与焦点控制点的交叉点关系。
   * 说明：
   * 1. 交点按两条线的无限延长线计算；
   * 2. 焦点控制点始终被约束到真实交点位置；
   * 3. 关系挂载后由倒角命令对完整活动草图统一参数求解。
   * @param firstLine 第一条线。
   * @param secondLine 第二条线。
   * @param focusPoint 焦点控制点。
   */
  public bindLineIntersectionRelation(
    firstLine: SketchLineObject,
    secondLine: SketchLineObject,
    focusPoint: SketchPointObject,
  ): void {
    // 创建交点约束实例。
    const intersectionConstraint = new LineIntersectionConstraint();
    firstLine.constraints.addRelation(
      intersectionConstraint as unknown as Parameters<typeof firstLine.constraints.addRelation>[0],
    );
    secondLine.constraints.addRelation(
      intersectionConstraint as unknown as Parameters<typeof secondLine.constraints.addRelation>[0],
    );
    focusPoint.constraints.addRelation(
      intersectionConstraint as unknown as Parameters<typeof focusPoint.constraints.addRelation>[0],
    );

	/** 交点焦点属于候选参数系统，不在此处选择隐式锚点直接传播。 */
  }
  //------ 倒角交叉点关系支持 结束------

  //------ 倒角角度约束支持 开始------
  /**
   * 绑定两条线的角度约束。
   * 说明：
   * 1. 第一条线作为锚线；
   * 2. 第二条线作为被驱动线；
   * 3. 约束会锁定当前夹角并在后续求解中维持不变。
   * @param anchorLine 锚线（通常为用户第一条选择线）。
   * @param movingLine 被驱动线（通常为新建倒角线）。
   */
  public bindLineAngleRelation(
    anchorLine: SketchLineObject,
    movingLine: SketchLineObject,
  ): void {
    /**
     * 角度数值必须在关系定义创建时冻结，不能依赖旧求解器首轮回填。
     * 两条线都属于当前参考面，使用局部二维方向计算稳定的有符号夹角分支。
     */
    const referencePlane = anchorLine.referencePlane;
    if (referencePlane !== movingLine.referencePlane) {
      throw new Error('倒角角度关系的两条线不属于同一草图参考面');
    }
    const anchorStart = referencePlane.worldToLocal(anchorLine.startPoint.getPosition());
    const anchorEnd = referencePlane.worldToLocal(anchorLine.endPoint.getPosition());
    const movingStart = referencePlane.worldToLocal(movingLine.startPoint.getPosition());
    const movingEnd = referencePlane.worldToLocal(movingLine.endPoint.getPosition());
    const anchorDirection = new THREE.Vector2(
      anchorEnd.x - anchorStart.x,
      anchorEnd.y - anchorStart.y,
    ).normalize();
    const movingDirection = new THREE.Vector2(
      movingEnd.x - movingStart.x,
      movingEnd.y - movingStart.y,
    ).normalize();
    const signedAngle = Math.atan2(
      anchorDirection.x * movingDirection.y - anchorDirection.y * movingDirection.x,
      THREE.MathUtils.clamp(anchorDirection.dot(movingDirection), -1, 1),
    );
    const angleConstraint = new LineAngleConstraint(signedAngle);
    anchorLine.constraints.addRelation(
      angleConstraint as unknown as Parameters<typeof anchorLine.constraints.addRelation>[0],
    );
    movingLine.constraints.addRelation(
      angleConstraint as unknown as Parameters<typeof movingLine.constraints.addRelation>[0],
    );

	/** 当前夹角由公式编译器从关系目标读取，批次统一求解时与其他关系共同生效。 */
  }
  //------ 倒角角度约束支持 结束------

  //------ 倒角点距约束支持 开始------
  /**
   * 绑定交点焦点与倒角新点之间的点距约束。
   * 说明：
   * 1. 第一目标点固定为锚点，当前专用于倒角交点焦点；
   * 2. 第二目标点固定为被驱动点，当前专用于倒角新创建的端点；
   * 3. 约束创建后由倒角命令统一参数求解，禁止对象级点位传播。
   * @param anchorPoint 锚点，通常为两条源线的交点焦点。
   * @param movingPoint 被驱动点，通常为倒角新点。
   * @param distance 目标距离。
   */
  public bindPointDistanceRelation(
    anchorPoint: SketchPointObject,
    movingPoint: SketchPointObject,
    distance: number,
  ): void {
    const distanceConstraint = new PointDistanceConstraint(distance);
    anchorPoint.constraints.addRelation(
      distanceConstraint as unknown as Parameters<typeof anchorPoint.constraints.addRelation>[0],
    );
    movingPoint.constraints.addRelation(
      distanceConstraint as unknown as Parameters<typeof movingPoint.constraints.addRelation>[0],
    );

	/** 点距候选只登记关系定义，统一求解器负责最终自由度分配。 */
  }
  //------ 倒角点距约束支持 结束------

  /**
   * 构建圆弧世界点集。
   * @param centerLocal 圆心局部坐标。
   * @param radius 半径。
   * @param startAngle 起始角度。
   * @param angleRange 角度范围。
   * @param clockwise 是否顺时针。
   * @param referencePlane 圆弧点集所属草图参考面。
   * @returns 世界点集。
   */
  public buildArcWorldPoints(
    centerLocal: THREE.Vector2,
    radius: number,
    startAngle: number,
    angleRange: number,
    clockwise: boolean,
    referencePlane: ReferencePlane = this.getReferencePlaneOrThrow(),
  ): THREE.Vector3[] {
    const steps = Math.max(16, Math.ceil(64 * Math.abs(angleRange) / (Math.PI * 2)));
    const arcPoints: THREE.Vector3[] = [];
    for (let index = 0; index <= steps; index += 1) {
      const progress = index / steps;
      const angle = clockwise
        ? startAngle - angleRange * progress
        : startAngle + angleRange * progress;
      arcPoints.push(referencePlane.localToWorld(
        new THREE.Vector2(
          centerLocal.x + radius * Math.cos(angle),
          centerLocal.y + radius * Math.sin(angle),
        ),
      ));
    }
    return arcPoints;
  }

  /**
   * 直接比较两条线的起点/终点，解析公共点。
   * @param firstLineData 第一条线。
   * @param secondLineData 第二条线。
   * @returns 公共点信息。
   */
  private resolveSharedCornerPoint(
    firstLineData: SketchCornerLineData,
    secondLineData: SketchCornerLineData,
  ): SketchSharedCornerPoint | null {
    const endpointPairs = [
      {
        firstPoint: firstLineData.startPoint,
        secondPoint: secondLineData.startPoint,
        firstCornerRole: SketchCornerPointRole.Start,
        secondCornerRole: SketchCornerPointRole.Start,
      },
      {
        firstPoint: firstLineData.startPoint,
        secondPoint: secondLineData.endPoint,
        firstCornerRole: SketchCornerPointRole.Start,
        secondCornerRole: SketchCornerPointRole.Terminal,
      },
      {
        firstPoint: firstLineData.endPoint,
        secondPoint: secondLineData.startPoint,
        firstCornerRole: SketchCornerPointRole.Terminal,
        secondCornerRole: SketchCornerPointRole.Start,
      },
      {
        firstPoint: firstLineData.endPoint,
        secondPoint: secondLineData.endPoint,
        firstCornerRole: SketchCornerPointRole.Terminal,
        secondCornerRole: SketchCornerPointRole.Terminal,
      },
    ];

    for (const endpointPair of endpointPairs) {
      if (!this.isSameCornerPoint(endpointPair.firstPoint, endpointPair.secondPoint)) {
        continue;
      }
      const firstWorld = endpointPair.firstPoint.getPosition();
      const secondWorld = endpointPair.secondPoint.getPosition();
      const cornerWorld = firstWorld.clone().add(secondWorld).multiplyScalar(0.5);
      return {
        sharedPoint: endpointPair.firstPoint,
        firstCornerRole: endpointPair.firstCornerRole,
        secondCornerRole: endpointPair.secondCornerRole,
        cornerWorld,
        cornerLocal: firstLineData.line.referencePlane.worldToLocal(cornerWorld),
      };
    }
    return null;
  }

  /**
   * 判断两个控制点是否表示同一个公共点。
   * @param firstPoint 第一个控制点。
   * @param secondPoint 第二个控制点。
   * @returns 是否为同一个公共点。
   */
  private isSameCornerPoint(firstPoint: SketchPointObject, secondPoint: SketchPointObject): boolean {
    if (firstPoint === secondPoint) {
      return true;
    }
    return firstPoint.getPosition().distanceTo(secondPoint.getPosition()) <= SKETCH_CLOSE_TOLERANCE;
  }

  /**
   * 根据角点所在端点角色解析线段使用信息。
   * @param lineData 线段数据。
   * @param cornerRole 角点所在端点角色。
   * @returns 使用信息。
   */
  private resolveLineUsageByCornerRole(
    lineData: SketchCornerLineData,
    cornerRole: SketchCornerPointRole,
  ): SketchCornerLineUsage | null {
    const isStartCorner = cornerRole === SketchCornerPointRole.Start;
    const cornerPoint = isStartCorner ? lineData.startPoint : lineData.endPoint;
    const farPoint = isStartCorner ? lineData.endPoint : lineData.startPoint;
    const cornerWorld = isStartCorner ? lineData.startWorld.clone() : lineData.endWorld.clone();
    const farWorld = isStartCorner ? lineData.endWorld.clone() : lineData.startWorld.clone();
    const cornerLocal = isStartCorner ? lineData.startLocal.clone() : lineData.endLocal.clone();
    const farLocal = isStartCorner ? lineData.endLocal.clone() : lineData.startLocal.clone();

    const directionLocal = farLocal.clone().sub(cornerLocal);
    if (directionLocal.lengthSq() <= SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }
    directionLocal.normalize();

    const farDistanceToCorner = farLocal.clone().sub(cornerLocal).dot(directionLocal);
    if (farDistanceToCorner <= SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }

    return {
      lineData,
      cornerPoint,
      cornerRole,
      cornerWorld,
      farPoint,
      farWorld,
      cornerLocal,
      farLocal,
      directionLocal,
      farDistanceToCorner,
    };
  }

  /**
   * 求解两条线段所在无限直线的交点（参考面局部坐标）。
   * @param firstLineData 第一条线段数据。
   * @param secondLineData 第二条线段数据。
   * @returns 交点局部坐标；平行或退化时返回 null。
   */
  private resolveInfiniteLineIntersectionLocal(
    firstLineData: SketchCornerLineData,
    secondLineData: SketchCornerLineData,
  ): THREE.Vector2 | null {
    const firstStart = firstLineData.startLocal;
    const firstDirection = firstLineData.endLocal.clone().sub(firstLineData.startLocal);
    const secondStart = secondLineData.startLocal;
    const secondDirection = secondLineData.endLocal.clone().sub(secondLineData.startLocal);
    const denominator = firstDirection.x * secondDirection.y - firstDirection.y * secondDirection.x;
    if (Math.abs(denominator) <= SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }

    const startDelta = secondStart.clone().sub(firstStart);
    const firstFactor = (startDelta.x * secondDirection.y - startDelta.y * secondDirection.x) / denominator;
    if (!Number.isFinite(firstFactor)) {
      return null;
    }
    return firstStart.clone().addScaledVector(firstDirection, firstFactor);
  }

  //------ 两直线圆角命中选支修复 开始------
  /**
   * 将有效鼠标命中世界坐标转换为参考面局部坐标。
   * @param hitWorld 鼠标命中世界坐标。
   * @param referencePlane 源实体所属草图参考面。
   * @returns 有效局部坐标；坐标缺失或包含非有限值时返回 null。
   */
  private resolveFiniteHitLocal(
    hitWorld: THREE.Vector3 | null,
    referencePlane: ReferencePlane,
  ): THREE.Vector2 | null {
    if (
      !hitWorld
      || ![hitWorld.x, hitWorld.y, hitWorld.z].every((coordinate) => Number.isFinite(coordinate))
    ) {
      return null;
    }

    const hitLocal = referencePlane.worldToLocal(hitWorld);
    return [hitLocal.x, hitLocal.y].every((coordinate) => Number.isFinite(coordinate))
      ? hitLocal
      : null;
  }

  /**
   * 基于虚拟角点（延长线交点）解析线段使用信息。
   *
   * 命中选支规则：
   * 1. 将命中点投影到源直线方向，确定用户点击的是交点哪一侧；
   * 2. 在该方向上选择距离虚拟交点更远的源端点作为保留端；
   * 3. 另一个源端点作为待替换角点，使正式提交只保留用户点击侧的线段；
   * 4. 命中信息不可用时沿用“离虚拟交点最近的端点作为角点”的兼容规则。
   *
   * @param lineData 线段数据。
   * @param virtualCornerLocal 虚拟角点局部坐标。
   * @param hitLocal 鼠标命中局部坐标。
   * @param referencePlane 源实体所属草图参考面。
   * @returns 使用信息。
   */
  private resolveLineUsageByVirtualCorner(
    lineData: SketchCornerLineData,
    virtualCornerLocal: THREE.Vector2,
    hitLocal: THREE.Vector2 | null = null,
    referencePlane: ReferencePlane = lineData.line.referencePlane,
  ): SketchCornerLineUsage | null {
    const lineDirection = lineData.endLocal.clone().sub(lineData.startLocal);
    if (lineDirection.lengthSq() <= SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }
    lineDirection.normalize();

    let cornerRole: SketchCornerPointRole | null = null;
    if (hitLocal) {
      const hitProgress = hitLocal.clone().sub(virtualCornerLocal).dot(lineDirection);
      if (Math.abs(hitProgress) > SKETCH_GEOMETRY_TOLERANCE) {
        /**
         * 把两个端点进度统一到命中方向后，分数更大的端点就是用户点击侧的保留远端。
         * 角点角色必须取保留端的相反端点，因为服务会用新切点替换该角色对应的源控制点。
         */
        const hitDirectionSign = hitProgress > 0 ? 1 : -1;
        const startRetainedScore = lineData.startLocal.clone()
          .sub(virtualCornerLocal)
          .dot(lineDirection) * hitDirectionSign;
        const endRetainedScore = lineData.endLocal.clone()
          .sub(virtualCornerLocal)
          .dot(lineDirection) * hitDirectionSign;
        if (Math.max(startRetainedScore, endRetainedScore) > SKETCH_GEOMETRY_TOLERANCE) {
          cornerRole = startRetainedScore >= endRetainedScore
            ? SketchCornerPointRole.Terminal
            : SketchCornerPointRole.Start;
        }
      }
    }

    if (!cornerRole) {
      // 无有效命中信息时保留既有行为，避免影响倒角和不携带 Intersection 的旧调用链。
      const distanceToStart = lineData.startLocal.distanceToSquared(virtualCornerLocal);
      const distanceToFinish = lineData.endLocal.distanceToSquared(virtualCornerLocal);
      cornerRole = distanceToStart <= distanceToFinish
        ? SketchCornerPointRole.Start
        : SketchCornerPointRole.Terminal;
    }

    const isStartCorner = cornerRole === SketchCornerPointRole.Start;
    const cornerPoint = isStartCorner ? lineData.startPoint : lineData.endPoint;
    const farPoint = isStartCorner ? lineData.endPoint : lineData.startPoint;
    const farWorld = isStartCorner ? lineData.endWorld.clone() : lineData.startWorld.clone();
    const farLocal = isStartCorner ? lineData.endLocal.clone() : lineData.startLocal.clone();
    const cornerWorld = referencePlane.localToWorld(virtualCornerLocal);
    const directionLocal = farLocal.clone().sub(virtualCornerLocal);
    if (directionLocal.lengthSq() <= SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }
    directionLocal.normalize();

    const farDistanceToCorner = farLocal.clone().sub(virtualCornerLocal).dot(directionLocal);
    if (farDistanceToCorner <= SKETCH_GEOMETRY_TOLERANCE) {
      return null;
    }

    return {
      lineData,
      cornerPoint,
      cornerRole,
      cornerWorld,
      farPoint,
      farWorld,
      cornerLocal: virtualCornerLocal.clone(),
      farLocal,
      directionLocal,
      farDistanceToCorner,
    };
  }
  //------ 两直线圆角命中选支修复 结束------
}
